import { useState, useEffect, useRef, useCallback, Fragment, Suspense, type PropsWithChildren, type ComponentProps } from 'react';
import { useMediaQuery } from "@uidotdev/usehooks";
import { SignedIn, SignedOut, useClerk } from '@clerk/clerk-react';
import { useNavigate, useLocation } from '@tanstack/react-router';
import { ErrorBoundary } from 'react-error-boundary';
import { useAuthorization, profileSelector} from '@/hooks/useAuthorization.hook';
import { useAlert } from '@/hooks/useAlert.hook';
import Authorized from '@/components/Authorized';
import NotAuthorized from '@/components/NotAuthorized';
import Alert from '@/components/Alert';
import AppBar from '../AppBar';
import PersonCheckIcon from '@/components/icons/PersonCheckIcon';
import PersonCancelIcon from '@/components/icons/PersonCancelIcon';
import MenuIcon from '@/components/icons/MenuIcon';
import IconButton from '@/components/IconButton';
import Branding from '@/components/Branding';
import Sidebar from '@/components/Sidebar';
import Dropdown from '@/components/Dropdown';
import DrawerOverlay from '@/components/DrawerOverlay';
import SidebarMenu from '@/components/SidebarMenu';
import Loading from '@/components/Loading';
import styles from './App.module.css';
import { SubscriptionLevel } from '@/models/profile';

interface AppProps extends PropsWithChildren, ComponentProps<"div"> {};

const drawerWidth = 340;

type ActionCallback = () => void;

interface MenuButtonProps {
    disabled: boolean;
    action?: ActionCallback;
}

function MenuButton(props: MenuButtonProps) {
    const fill = props.disabled ? "#E0E0E0" : "#0090F2"
    return (
        <IconButton
            padding="2px"
            className={styles.menu}
            aria-label="open sidebar"
            onClick={props.action}
            disabled={props.disabled}
        >
            <MenuIcon size={{ width: 24, height: 24 }} fill={fill} className={styles.menuIcon} />
        </IconButton>
    )
}

interface MobileMenuProps {
    mobile: boolean;
    action: ActionCallback;
}

function MobileMenu(props: MobileMenuProps) {
    if (!props.mobile) {
        return null;
    }

    return (
        <Fragment>
            <Authorized>
                <MenuButton action={props.action} disabled={false} />
            </Authorized>
            <NotAuthorized>
                <MenuButton disabled={true} />
            </NotAuthorized>
        </Fragment>
    )
}

interface SidebarContentProps {
    onClose?: ActionCallback;
}

function SidebarContent(props: SidebarContentProps) {
    return (
        <Fragment>
            <Authorized>
                <Suspense fallback={<Loading size="lg" message="Loading..." />}>
                    <SidebarMenu onClose={props.onClose} />
                </Suspense>
            </Authorized>
            <NotAuthorized>
                <div className={styles.notAuthorized}>
                    Please sign in
                </div>
            </NotAuthorized>
        </Fragment>
    )
}

interface MenuItemProps extends ComponentProps<"div"> {
    label: string;
    action?: VoidFunction;
}

function MenuItem(props: MenuItemProps) {
    const { label, action, ...divProps } = props;
    const onClick = props.action ? props.action : () => {};
    return (
        <div className={styles.menuItem} {...divProps}>
            <button className={styles.menuItemButton} onClick={onClick}>
                {props.label}
            </button>
        </div>
    )
}

function MenuDivider() {
    return (
        <div className={styles.menuDivider}></div>
    )
}

interface ProfileMenuProps  {
    closeCallback: () => void;
}

function ProfileMenu(props: ProfileMenuProps) {
    const location = useLocation();
    const [showingProfile, setShowingProfile] = useState(true);
    const { signOut } = useClerk();

    const closeMenu = () => {
        props.closeCallback();
    }
    const navigate = useNavigate();

    useEffect(() => {
        setShowingProfile(location.pathname.includes('/profile'));
    }, []);

    const openProfile = () => {
        closeMenu();
        navigate({ 
            to: "/profile", 
            search: {
                next: location.pathname
            },
            viewTransition: true 
        });
    }

    const signOutClick = () => {
        closeMenu();
        signOut({redirectUrl: '/'});
    }

    return (
        <div className={styles.profileMenu}>
            { !showingProfile && 
                ( 
                    <Fragment>
                        <MenuItem label="Profile..." action={openProfile} />
                        <MenuDivider />
                    </Fragment>
                )
            }
            <MenuItem label="Sign out" action={signOutClick} />
        </div>
    )
}

function ProfileMenuButton(props: ComponentProps<'div'>) {
    const signedInFill = "#00aa00";
    const signedOutFill = "#d32f2f";
    return (
        <div {...props}>
            <SignedIn>
                <IconButton
                    className={styles.tool}
                    aria-label="open profile" 
                >   
                    <PersonCheckIcon size={{ width: 20, height: 20 }} fill={signedInFill} position={{ x: 0, y: -6 }} />
                </IconButton>
            </SignedIn>
            <SignedOut>
                <IconButton
                    className={styles.tool}
                    aria-label="open profile" 
                    disabled
                >   
                    <PersonCancelIcon size={{ width: 20, height: 20 }} fill={signedOutFill} position={{ x: 0, y: -6 }} />
                </IconButton>
            </SignedOut>
        </div>
    )
}

export default function App(props: AppProps) {
    const { descriptor, showing, hide } = useAlert();
    const isSmallDevice = useMediaQuery("only screen and (max-width : 768px)");
    const profile = useAuthorization(profileSelector);
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(true);
    const [subscribed, setSubscribed] = useState(false);
    const menuButtonRef = useRef(null);

    useEffect(() => {
        if (profile) {
            setSubscribed(profile.subscription.type != SubscriptionLevel.NONE);
        }
        setBusy(false);
    }, [profile]);

    const mainContentStyles = {
        width: "100%"
    };
    if (subscribed && !isSmallDevice) {
        mainContentStyles.width = `calc(100% - ${drawerWidth}px)`
    }

    const openDrawer = useCallback(() => {
        setOpen(true);
    }, []);

    const onClose = useCallback(() => {
        setOpen(false);
    }, []);

    const closeCallback = useCallback(() => {
        const refElem = menuButtonRef.current;
        if (refElem) {
            const elem = refElem as HTMLElement;
            elem.click();
        }
    }, []);

    if (busy) {
        <Loading />
    }
    
    return (
        <ErrorBoundary fallback={<div>Something went wrong</div>}>
            <div className={styles.page}>
                <AppBar>
                    <div className={styles.headerLeft}>
                        { subscribed && <MobileMenu mobile={isSmallDevice} action={openDrawer} /> }
                        <Branding className={styles.branding} width={130} height={40} />
                    </div>
                    <div className={styles.headerRight}>
                        <Dropdown trigger={(<ProfileMenuButton ref={menuButtonRef} className={styles.profileTrigger} />)} position="bottom-right">
                            <ProfileMenu closeCallback={closeCallback} />
                        </Dropdown>
                    </div>
                </AppBar>
                <div className={styles.workspace}>
                    { 
                        subscribed &&
                        !isSmallDevice && 
                            <Sidebar width={drawerWidth}>
                                <SidebarContent />
                            </Sidebar>
                    }
                    <div className={styles.mainContent} style={mainContentStyles}>
                        {props.children}
                    </div>
                </div>
            </div> 
            {
                subscribed && 
                isSmallDevice && 
                    <DrawerOverlay open={open} onClosed={onClose} drawerWidth={drawerWidth}>
                        <SidebarContent onClose={onClose} />
                    </DrawerOverlay>
            }
            <Alert
                mode={descriptor.mode}
                generalMessage={descriptor.generalMessage}
                detailMessage={descriptor.detailMessage} 
                isVisible={showing}
                onClose={() => hide()}
            />
        </ErrorBoundary>
    )
}

